import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const CompareContext = createContext();

const COMPARE_STORAGE_KEY = 'velaris_compare_cars';
const MAX_COMPARE_CARS = 4;

const normalizeCompareCar = (car) => ({
  id: car.id,
  brand: car.brand || '',
  model: car.model || '',
  year: car.year || '',
  image: car.image || '',
  pricePerDay: car.pricePerDay || 0,
  category: car.category || '',
  city: car.city || '',
  seats: car.seats || 0,
  transmission: car.transmission || '',
  fuelType: car.fuelType || '',
  engine: car.engine || '',
  power: car.power || '',
  supplier: car.supplier || '',
  supplierPhone: car.supplierPhone || '',
  supplierWhatsApp: car.supplierWhatsApp || car.supplierPhone || '',
  categorySlug: car.categorySlug || '',
});

const readStoredCars = () => {
  try {
    const stored = localStorage.getItem(COMPARE_STORAGE_KEY);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CompareProvider = ({ children }) => {
  const compareCarsRef = useRef(readStoredCars());
  const [compareCars, setCompareCars] = useState(() => compareCarsRef.current);

  useEffect(() => {
    localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(compareCars));
  }, [compareCars]);

  const isInCompare = useCallback(
    (carId) => compareCarsRef.current.some((car) => String(car.id) === String(carId)),
    [],
  );

  const addToCompare = useCallback(
    (car) => {
      if (!car?.id) {
        return { success: false, reason: 'invalid' };
      }

      const currentCars = compareCarsRef.current;

      if (currentCars.some((item) => String(item.id) === String(car.id))) {
        return { success: false, reason: 'duplicate' };
      }

      if (currentCars.length >= MAX_COMPARE_CARS) {
        return { success: false, reason: 'limit' };
      }

      const nextCars = [...currentCars, normalizeCompareCar(car)];
      compareCarsRef.current = nextCars;
      setCompareCars(nextCars);
      return { success: true };
    },
    [],
  );

  const removeFromCompare = useCallback((carId) => {
    const nextCars = compareCarsRef.current.filter((car) => String(car.id) !== String(carId));
    compareCarsRef.current = nextCars;
    setCompareCars(nextCars);
  }, []);

  const clearCompare = useCallback(() => {
    compareCarsRef.current = [];
    setCompareCars([]);
  }, []);

  const value = useMemo(
    () => ({
      compareCars,
      maxCompareCars: MAX_COMPARE_CARS,
      canOpenComparison: compareCars.length >= 2,
      isInCompare,
      addToCompare,
      removeFromCompare,
      clearCompare,
    }),
    [addToCompare, clearCompare, compareCars, isInCompare, removeFromCompare],
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};

export default CompareContext;
